import { format, isValid, parseISO } from 'date-fns'
import { isDateControlKey, isPhoneKey, isAmountKey, YARDI_GUEST_SEARCH_CRITERIA_KEYS } from '../constants'

/**
 * Occupants "Invite Sent on" — Angular format `MM/dd/yyyy hh:mm:ss a`.
 * API sends unix seconds (e.g. 1785009187); convert to ms before formatting.
 */
export function formatInviteSentOn(value: string | number | null | undefined): string {
  if (value == null || value === '') return ''

  const trimmed = String(value).trim()
  const numeric = typeof value === 'number' ? value : Number(trimmed)
  if (!Number.isNaN(numeric) && trimmed.length <= 13) {
    const ms = numeric > 1e12 ? numeric : numeric * 1000
    const d = new Date(ms)
    if (isValid(d)) return format(d, 'MM/dd/yyyy hh:mm:ss a')
  }

  const d = new Date(trimmed)
  if (isValid(d)) return format(d, 'MM/dd/yyyy hh:mm:ss a')
  return trimmed
}

/** Format a date value as `yyyy-MM-dd`. */
export function formatDateYardi(value: unknown): string {
  if (value == null || value === '') return value as string
  if (value instanceof Date) {
    return isValid(value) ? format(value, 'yyyy-MM-dd') : String(value)
  }
  if (typeof value === 'string') {
    const parsed = parseISO(value.replace(' ', 'T'))
    if (isValid(parsed)) return format(parsed, 'yyyy-MM-dd')
    const d = new Date(value)
    if (isValid(d)) return format(d, 'yyyy-MM-dd')
  }
  return String(value)
}

/** Recursively normalizes a Yardi payload in place (phones, dates, amounts). */
export function validateData<T extends Record<string, unknown>>(
  data: T | null | undefined,
): T | null | undefined {
  if (!data) return data
  Object.keys(data).forEach((key) => {
    const v = data[key]
    if (v != null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date) && !isDateControlKey(key)) {
      validateData(v as Record<string, unknown>)
    } else {
      if (v) {
        if (typeof v === 'string' && isPhoneKey(key)) {
          ;(data as Record<string, unknown>)[key] = v.replace(/\D/g, '')
        } else if (isDateControlKey(key)) {
          if (v instanceof Date || typeof v === 'string') {
            ;(data as Record<string, unknown>)[key] = formatDateYardi(v)
          }
        } else if (typeof v === 'string' && isAmountKey(key)) {
          ;(data as Record<string, unknown>)[key] = String(v).replace(/[^0-9.]+/g, '')
        }
      } else {
        if (key === 'middle_name') {
          delete data[key]
        }
      }
    }
  })
  return data
}

/** Trim string values and omit empties; expands a `daterange` object into its keys. */
export function buildTrimmedParams(
  values: Record<string, unknown>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const key of Object.keys(values)) {
    const raw = values[key]
    if (raw != null && typeof raw === 'object' && key === 'daterange') {
      const dr = raw as Record<string, unknown>
      Object.keys(dr).forEach((k) => {
        if (dr[k]) {
          out[k] = String(dr[k])
        }
      })
      continue
    }
    const coerced =
      typeof raw === 'string' ? raw.trim() : raw === undefined || raw === null ? '' : raw
    if (coerced !== '' && coerced != null) {
      if (typeof coerced === 'boolean' || typeof coerced === 'number') {
        out[key] = coerced
      } else {
        out[key] = String(coerced)
      }
    }
  }
  return out
}

/** True if at least one optional guest-search field is provided. */
export function hasAtLeastOneYardiGuestSearchCriterion(
  trimmed: Record<string, string | number | boolean>,
): boolean {
  return YARDI_GUEST_SEARCH_CRITERIA_KEYS.some((key) => {
    const v = trimmed[key]
    if (v === undefined || v === null) return false
    if (typeof v === 'boolean') return false
    return String(v).trim().length > 0
  })
}

/** True if the error signals missing guest-search criteria. */
export function isYardiGuestSearchCriteriaValidationError(error: unknown): boolean {
  const ax = error as { response?: { data?: unknown } }
  const data = ax?.response?.data
  let text = ''
  if (typeof data === 'string') {
    text = data
  } else if (data && typeof data === 'object' && data !== null) {
    const errVal = (data as { error?: unknown }).error
    if (typeof errVal === 'string') {
      text = errVal
    } else {
      text = JSON.stringify(data)
    }
  }
  return text.toLowerCase().includes('at least one search filter')
}
